/* eslint-disable @typescript-eslint/no-explicit-any */
import { generateTableSQLFromObject, getColumnAffinity } from '@/lib/Sqlite/schema'
import { getRandomString, getUnixtime, getUnixtimeFromString } from '@/lib/Functions'
import { App } from '@/app'
import { Toast } from '@/app/toast'
import { SQL } from '@/lib/Sqlite'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { Device } from '@capacitor/device'
import { App as CapacitorApp } from '@capacitor/app'
import CryptoJS from 'crypto-js'
import { encryptDataChunked, yieldToMain } from './backupCrypto'
import { GCS_ROOT_PATH, IS_PRODUCTION, isApp } from '..'
import { Network } from '@capacitor/network'
import { CloudFile } from '@/lib/CloudFile'
import _, { find, get } from 'lodash'
import { QuickInputItem, UserSettingConfig, UserSettingConfigSettings } from './data/type'
import { Browser } from '@capacitor/browser'
import { PHOTO_DIR } from './constant'
import { Lib } from '@/lib'
import { Capacitor } from '@capacitor/core'
import { Defect, DefectPhoto, Task } from './data/types'
import SQLite, { columnCache, columnAffinityCache } from '../lib/Task/sqlite'
import {
  generateKeyBetween as originalGenerateKeyBetween,
  generateNKeysBetween,
} from 'fractional-indexing'
import Merge from '../lib/Task/merge'
import { useAllTaskStore } from './store/task'
import { API_URL } from './data/enum/api'
import type { CanvasEngineExportEntityType } from '@/lib/CanvasEngine/types'

// 일괄 스타일 변경에서 크기(스케일) 계열 필드 키
const SHAPE_SCALE_KEYS = [
  'balloonScale',
  'connectedLineThicknessScale',
  'balloonFontScale',
  'pointShapeScale',
  'areaBorderThicknessScale',
] as const

/**
 * 일괄 스타일 데이터에서 크기 계열 필드만 추출한다.
 * 값이 없는(미적용) 필드는 제외해 기존 값이 undefined로 덮이지 않게 한다.
 * @param shapeCanvasData - 부분 적용될 수 있는 스타일 데이터
 */
export const pickShapeScaleFields = (
  shapeCanvasData: Partial<CanvasEngineExportEntityType>,
): Partial<CanvasEngineExportEntityType> => {
  const result: Record<string, unknown> = {}
  for (const key of SHAPE_SCALE_KEYS) {
    const value = shapeCanvasData[key]
    if (value !== undefined) result[key] = value
  }
  return result as Partial<CanvasEngineExportEntityType>
}

// confirm(전역 확인 모달)이 떠 있는지 여부.
// 떠 있으면 하위 모달/패널의 ESC 닫기를 막아 confirm이 ESC를 우선 처리하도록 한다.
export function isConfirmModalOpen(): boolean {
  return App.confirmInstance.get().value.isShow
}

// 현재 포커스가 입력 요소(input/textarea/contenteditable)면 blur하고 true를 반환한다.
// ESC 1차 입력에서 포커스만 해제하고 모달/패널 닫기는 다음 ESC로 넘기는 데 사용한다.
export function blurActiveInputIfFocused(): boolean {
  const active = document.activeElement as HTMLElement | null
  if (
    active &&
    (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
  ) {
    active.blur()
    return true
  }
  return false
}

/**
 * generateKeyBetween 래퍼 함수
 * 동일한 sort_key 사이에 삽입 시도 시 fallback 처리
 *
 * @param a - 이전 키 (null 가능)
 * @param b - 다음 키 (null 가능)
 * @returns 생성된 sort_key
 */
export const generateKeyBetween = (
  a: string | null | undefined,
  b: string | null | undefined,
): string => {
  try {
    return originalGenerateKeyBetween(a ?? null, b ?? null)
  } catch (error) {
    console.warn(
      `[generateKeyBetween] Invalid keys detected (a: ${a}, b: ${b}). Using fallback.`,
      error,
    )

    // Fallback 전략
    if (a && !b) {
      // a 다음에 추가하려는데 실패한 경우
      return originalGenerateKeyBetween(null, null)
    } else if (!a && b) {
      // b 이전에 추가하려는데 실패한 경우
      return originalGenerateKeyBetween(null, null)
    } else {
      // a와 b 사이에 추가하려는데 실패한 경우 (a === b)
      // a 다음에 추가 시도
      try {
        return originalGenerateKeyBetween(a ?? null, null)
      } catch {
        // 그것도 실패하면 새로운 키 생성
        return originalGenerateKeyBetween(null, null)
      }
    }
  }
}

// fractional-indexing 규칙상 유효한 order key인지 검사한다.
// 서버 컬럼 기본값 'a'나 레거시 값은 무효 키이며, 이 위에서 재정렬하면
// 사이 키 생성이 실패(내부 throw→fallback)해 엉뚱한 위치로 가거나 중복 키가 쌓인다.
export const isValidOrderKey = (key: string | null | undefined): boolean => {
  if (key == null || key === '') return false
  try {
    // 유효한 키여야 그 다음 키 생성이 성공한다 (검증용, 결과는 버림)
    originalGenerateKeyBetween(key, null)
    return true
  } catch {
    return false
  }
}

// 두 이웃 키 사이에 새 키를 끼울 수 있는지 판별한다.
// 한쪽이 끝(null)이면 존재하는 쪽만 유효하면 되고,
// 둘 다 있으면 모두 유효하고 a < b(엄격 증가)여야 한다.
export const canInsertBetween = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const aOk = a == null || isValidOrderKey(a)
  const bOk = b == null || isValidOrderKey(b)
  if (!aOk || !bOk) return false
  if (a != null && b != null) return a < b
  return true
}

// 0번부터 순차적인 유효한 sort_key n개를 생성한다 (['a0','a1',...]).
// 무효/중복 키가 섞인 목록을 깨끗한 키로 재배치(self-heal)할 때 사용.
export const generateFreshSortKeys = (count: number): string[] => {
  if (count <= 0) return []
  return generateNKeysBetween(null, null, count)
}

export async function mergeServerToLocalServerFirst(
  target: (any & { modifiedFromLocal: boolean; localStringId: string })[],
  remoteDatas: any[],
): Promise<any[]> {
  // 원본 target 배열 복사본 생성
  const result = [...target]

  // 로컬 ID 맵 생성 (O(1) 검색을 위해)
  const localIdMap = new Map()
  const localStringIdMap = new Map() // localStringId 기반 맵 추가
  result.forEach(item => {
    const id = _.get(item, 'id')
    if (id !== undefined) {
      localIdMap.set(String(id), item)
    }
    // localStringId로도 인덱싱 (race condition 대응)
    const localStringId = _.get(item, 'localStringId')
    if (localStringId) {
      localStringIdMap.set(String(localStringId), item)
    }
  })

  // 서버 데이터 ID 맵 생성 (서버에 없는 로컬 데이터를 찾기 위해)
  const remoteIdMap = new Set()
  remoteDatas.forEach(item => {
    const id = _.get(item, 'id')
    if (id !== undefined) {
      remoteIdMap.add(String(id))
    }
  })

  // 서버 데이터 처리
  for (const remoteItem of remoteDatas) {
    if (!remoteItem) continue

    const remoteId = _.get(remoteItem, 'id')
    if (remoteId === undefined) continue

    // 1차: ID로 매칭 시도
    let localItem = localIdMap.get(String(remoteId))

    // 2차: ID 매칭 실패 시 localStringId로 매칭 시도 (race condition 대응)
    if (!localItem) {
      const remoteLocalStringId = _.get(remoteItem, 'localStringId')
      if (remoteLocalStringId) {
        localItem = localStringIdMap.get(String(remoteLocalStringId))
        if (localItem) {
          console.info(
            `🔗 localStringId로 매칭 성공: ${remoteLocalStringId} (서버 ID: ${remoteId}, 로컬 ID: ${localItem.id})`,
          )
        }
      }
    }

    if (localItem) {
      // facilities 필드는 오프라인 우선 처리
      const localFacilities = localItem.facilities
      const remoteFacilities = remoteItem.facilities

      // modified_at 필드도 오프라인 우선 처리
      const localModifiedAt = localItem.modified_at
      const remoteModifiedAt = remoteItem.modified_at

      // 로컬에 facilities 데이터가 있고, 서버에는 빈 배열이거나 없을 때 로컬 유지
      const shouldPreserveLocalFacilities =
        localFacilities &&
        Array.isArray(localFacilities) &&
        localFacilities.length > 0 &&
        (!remoteFacilities || !Array.isArray(remoteFacilities) || remoteFacilities.length === 0)

      // 로컬에 modified_at 데이터가 있고, 서버 값보다 클 때 로컬 유지
      // 또는 서버에 modified_at이 없을 때 로컬 유지
      const shouldPreserveLocalModifiedAt =
        localModifiedAt !== undefined &&
        localModifiedAt !== null &&
        localModifiedAt > 0 &&
        (!Object.prototype.hasOwnProperty.call(remoteItem, 'modified_at') ||
          remoteModifiedAt === undefined ||
          remoteModifiedAt === null ||
          localModifiedAt > remoteModifiedAt)

      // 로컬에 있는 항목은 서버 데이터로 덮어쓰기
      Object.assign(localItem, remoteItem)

      // facilities는 로컬 데이터 우선 유지
      if (shouldPreserveLocalFacilities) {
        localItem.facilities = localFacilities
        console.info(
          `오프라인 우선: ID ${remoteId}의 facilities 로컬 데이터 유지 (${localFacilities.length}개)`,
        )
      }

      // modified_at은 로컬 데이터 우선 유지
      if (shouldPreserveLocalModifiedAt) {
        localItem.modified_at = localModifiedAt
        console.info(
          `오프라인 우선: ID ${remoteId}의 modified_at 로컬 데이터 유지 (local: ${localModifiedAt}, remote: ${remoteModifiedAt})`,
        )
      }

      // 로컬에서 삭제된 항목이면 deleted를 0으로 초기화
      if (localItem.deleted_at) {
        localItem.deleted_at = null
      }
    } else {
      // 로컬에 없는 항목은 새로 추가
      result.push({ ...remoteItem })
    }
  }

  // 서버에 없고 로컬에만 있는 데이터 삭제 처리
  result.forEach(item => {
    const itemId = _.get(item, 'id')
    if (itemId !== undefined && !remoteIdMap.has(String(itemId))) {
      item.deleted_at = Merge.getCurrentTime()
      console.info(`서버에 없는 로컬 데이터 삭제 처리: ID ${itemId}`)
    }
  })

  return result
}

export type SQLiteColumnType = 'INTEGER' | 'TEXT' | 'REAL' | 'BLOB' | 'BOOLEAN' | 'TIMESTAMP'

export async function getDynamicTableSql(
  tableName: string,
  data: any,
  isAlone: boolean = false,
): Promise<string | undefined> {
  let createTableSQL
  const options = {
    idField: 'id',
    idType: 'TEXT' as SQLiteColumnType,
    includeDeletedField: true,
  }
  try {
    if (Array.isArray(data)) {
      // 배열인 경우
      if (data.length > 0) {
        if (!isAlone) {
          data[0].task_id = 0
        }
        createTableSQL = generateTableSQLFromObject(tableName, data[0], options)
      }
    } else {
      // 단일 객체인 경우
      if (!isAlone) {
        data.task_id = 0
      }
      createTableSQL = generateTableSQLFromObject(tableName, data, options)
    }
    return createTableSQL
  } catch (error) {
    console.error(`Error creating dynamic table ${tableName}:`, error)
    return ''
  }
}

export function parseValueFromDB(value: any): any {
  // 문자열이고 JSON 형식으로 보이면 파싱 시도
  if (
    typeof value === 'string' &&
    (value.startsWith('{') || value.startsWith('[')) &&
    (value.endsWith('}') || value.endsWith(']'))
  ) {
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }
  return value
}
// 단일 row 파싱 헬퍼 메서드
export function parseRow(row: any): any {
  const parsedRow: any = {}
  for (const [field, value] of Object.entries(row)) {
    parsedRow[field] = parseValueFromDB(value)
  }
  return parsedRow
}

// 백업 관련 상수 정의

const getBackupBasePath = (): string => {
  const platform = Capacitor.getPlatform()
  if (platform === 'ios')
    return 'zentrix_backups' // iOS
  else return '/zentrix' // Android
}

const BACKUP_RETENTION_DAYS = 7
// 백업은 임시 파일에 조각으로 쓴 뒤 이름을 바꾼다 — 중간에 앱이 종료돼도 잘린 백업이 Backup_*.json으로 남지 않게
const BACKUP_PART_SUFFIX = '.part'
// 기록 도중 종료돼 남은 임시 파일 정리 기준
const BACKUP_PART_RETENTION_MS = 60 * 60 * 1000
// writeFile 브리지 호출 1회의 크기. A21s에서 1.95MB 한 번에 약 0.6초, 128KB도 약 75ms 정지 → 64KB
const BACKUP_WRITE_CHUNK_CHARS = 64 * 1024

/**
 * 오래된 백업 파일을 삭제합니다.
 * @param taskName - 과업 이름
 */
async function cleanupOldBackups(taskName: string): Promise<void> {
  try {
    const backupDir = `${getBackupBasePath()}/${taskName}`
    let result
    try {
      result = await Filesystem.readdir({
        path: backupDir,
        directory: Directory.Documents,
      })
    } catch (readdirError) {
      const platform = Capacitor.getPlatform()
      let errorMessage = ''

      if (readdirError instanceof Error) {
        errorMessage = readdirError.message
      } else {
        errorMessage = String(readdirError)
      }

      if (
        platform === 'ios' &&
        (errorMessage.includes('No such file or directory') ||
          errorMessage.includes('does not exist') ||
          errorMessage.includes('존재하지 않습니다'))
      ) {
        console.info(`Backup directory does not exist yet: ${backupDir}`)
        return // 디렉토리가 없으면 종료
      }

      throw readdirError
    }

    const now = new Date()
    const retentionTime = BACKUP_RETENTION_DAYS * 24 * 60 * 60 * 1000 // 7일을 밀리초로 변환

    for (const file of result.files) {
      const isPart = file.name.endsWith(`.json${BACKUP_PART_SUFFIX}`)
      if (file.name.startsWith('Backup_') && (file.name.endsWith('.json') || isPart)) {
        const filePath = `${backupDir}/${file.name}`
        const fileInfo = await Filesystem.stat({
          path: filePath,
          directory: Directory.Documents,
        })

        // 파일 수정 시간을 밀리초로 변환
        const fileAge = now.getTime() - fileInfo.mtime * 1000
        if (fileAge > (isPart ? BACKUP_PART_RETENTION_MS : retentionTime)) {
          try {
            await Filesystem.deleteFile({
              path: filePath,
              directory: Directory.Documents,
            })
            console.info(`Deleted old backup: ${file.name}`)
          } catch (deleteError) {
            // 개별 파일 삭제 실패는 로그만 남기고 계속 진행
            console.warn(`Failed to delete old backup ${file.name}:`, deleteError)
          }
        }
      }
    }
  } catch (error) {
    console.error('Error cleaning up old backups:', error)
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

/**
 * 백업 디렉토리를 생성합니다.
 * @param taskName - 과업 이름
 */
async function ensureBackupDirectory(taskName: string): Promise<string> {
  const platform = Capacitor.getPlatform()
  const backupDir = `${getBackupBasePath()}/${taskName}`

  try {
    // 디렉토리 생성 시도
    await Filesystem.mkdir({
      path: backupDir,
      directory: Directory.Documents,
      recursive: true,
    })
    console.info(`Backup directory created: ${backupDir}`)
    return backupDir
  } catch (error) {
    const errorMessage = getErrorMessage(error)

    // 이미 존재하는 경우
    if (
      ['Directory exists'].includes(errorMessage) ||
      /exists|already exists|File exists/i.test(errorMessage)
    ) {
      console.info(`Backup directory already exists: ${backupDir}`)
      return backupDir
    }

    // iOS에서 이름 충돌 처리
    if (
      platform === 'ios' &&
      /(동일한 이름을 가진 파일이 이미 존재|cannot save|저장할 수 없습니다)/i.test(errorMessage)
    ) {
      console.warn(
        `iOS에서 백업 디렉토리 이름 충돌 감지, 기존 항목 삭제 후 재시도: ${errorMessage}`,
      )

      try {
        const baseDir = getBackupBasePath()
        await Filesystem.deleteFile({
          path: baseDir,
          directory: Directory.Documents,
        })
        console.info(`기존 백업 항목 삭제됨: ${baseDir}`)

        await Filesystem.mkdir({
          path: backupDir,
          directory: Directory.Documents,
          recursive: true,
        })
        console.info(`백업 디렉토리 재생성 완료: ${backupDir}`)
        return backupDir
      } catch (deleteError) {
        console.error(`기존 백업 항목 삭제 실패: ${getErrorMessage(deleteError)}`)

        // 다른 이름으로 시도
        const timestamp = Date.now()
        const alternativeDir = `${getBackupBasePath()}_${timestamp}/${taskName}`

        try {
          await Filesystem.mkdir({
            path: alternativeDir,
            directory: Directory.Documents,
            recursive: true,
          })
          console.info(`대체 백업 디렉토리 생성: ${alternativeDir}`)
          return alternativeDir
        } catch (altError) {
          console.error(`대체 백업 디렉토리 생성도 실패: ${getErrorMessage(altError)}`)
          throw altError // 최종 실패
        }
      }
    }

    // 권한 문제인 경우
    if (
      platform === 'ios' &&
      /(permission denied|access denied|not permitted)/i.test(errorMessage)
    ) {
      console.warn(`iOS 백업 디렉토리 생성 실패 - 백업 기능 비활성화: ${errorMessage}`)
      throw new Error('BACKUP_DISABLED_IOS')
    }

    // 그 외 에러는 그대로 던짐
    throw error
  }
}

// 암호화 함수
export function encryptData(data: string): string {
  try {
    // 암호화 키 검증
    const encryptKey = import.meta.env.VITE_BACKUP_ENCRYPT_KEY
    if (!encryptKey) {
      throw new Error('암호화 키가 설정되지 않았습니다.')
    }

    // 키를 UTF-8로 파싱
    const keyWordArray = CryptoJS.enc.Utf8.parse(encryptKey)
    // IV 생성
    const iv = CryptoJS.lib.WordArray.random(16)

    // 데이터 암호화
    const encrypted = CryptoJS.AES.encrypt(data, keyWordArray, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    })

    // IV와 암호화된 데이터를 Base64로 변환하여 결합
    const ivBase64 = CryptoJS.enc.Base64.stringify(iv)
    const encryptedBase64 = encrypted.toString()

    return `${ivBase64}:${encryptedBase64}`
  } catch (error) {
    console.error('Encryption error:', error)
    throw new Error('Failed to encrypt data: ' + (error as Error).message)
  }
}

// 복호화 함수
export function decryptData(encryptedData: string): string {
  try {
    // 암호화 키 검증
    const encryptKey = import.meta.env.VITE_BACKUP_ENCRYPT_KEY
    if (!encryptKey) {
      throw new Error('암호화 키가 설정되지 않았습니다.')
    }

    // IV와 암호화된 데이터 분리
    const parts = encryptedData.split(':')
    if (parts.length !== 2) {
      throw new Error('Invalid encrypted data format')
    }

    const ivBase64 = parts[0]
    const encryptedBase64 = parts[1]

    // 키를 UTF-8로 파싱
    const keyWordArray = CryptoJS.enc.Utf8.parse(encryptKey)
    // IV를 Base64에서 복원
    const iv = CryptoJS.enc.Base64.parse(ivBase64)

    // 데이터 복호화
    const decrypted = CryptoJS.AES.decrypt(encryptedBase64, keyWordArray, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    })

    // 복호화된 데이터를 UTF-8 문자열로 변환
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8)

    if (!decryptedString) {
      throw new Error('Decryption failed: empty result')
    }

    return decryptedString
  } catch (error) {
    console.error('Decryption error:', error)
    throw new Error('Failed to decrypt data: ' + (error as Error).message)
  }
}

export async function backupSQLiteToLocalFile(taskId: string, taskName: string): Promise<void> {
  console.info(`Starting backup for task: ${taskName} (ID: ${taskId})`)

  try {
    // 백업 디렉토리 생성 및 실제 사용할 경로 확인
    const actualBackupDir = await ensureBackupDirectory(taskName)

    // 디바이스 ID 가져오기
    const deviceInfo = await Device.getId()
    const deviceId = deviceInfo.identifier || 'unknown'

    // 현재 날짜/시간 포맷팅 (현지 시간대 반영)
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    const dateStr = `${year}${month}${day}_${hours}${minutes}`

    // 백업 파일 경로 및 이름 생성
    const backupFileName = `Backup_${deviceId}_${taskName}_${dateStr}`
    const backupFullPath = `${actualBackupDir}/${backupFileName}.json`
    console.info(`Backup will be saved to: ${backupFullPath}`)

    // 현재 DB 연결 가져오기
    const db = await SQL.getDatabase()

    // 백업할 테이블 목록
    // device_event_log는 제외 — 복구에 불필요한 변경 이력이며, 로그가 수만 건 누적된 단말에서
    // 백업 직렬화(JSON.stringify → base64 → Filesystem.writeFile 브리지)가 OOM 크래시를 유발함
    // (실기기 재현: logs/perf-20260610-152233.log — Bridge.callPluginMethod OOM FATAL)
    const tables = SQLite.getCurrentTables().filter(table => table !== 'device_event_log')

    try {
      // 각 테이블의 데이터를 JSON으로 내보내기
      const exportData: any = {
        database: taskName,
        version: 1,
        encrypted: true,
        mode: 'full',
        tables: [],
      }

      for (const table of tables) {
        try {
          // 테이블 스키마 가져오기
          const schemaResult = await db.query(
            // eslint-disable-next-line quotes
            "SELECT sql FROM sqlite_master WHERE type='table' AND name=?",
            [table],
          )
          if (schemaResult.values && schemaResult.values.length > 0) {
            const createTableSQL = schemaResult.values[0].sql

            // 테이블 데이터 가져오기
            const data = await db.query(`SELECT * FROM ${table} WHERE task_id = ?`, [taskId])

            if (data.values && data.values.length > 0) {
              // 컬럼 정보 가져오기
              const columns = Object.keys(data.values[0])

              // 테이블 스키마 파싱
              const schema = columns.map(column => ({
                column,
                value: createTableSQL.match(new RegExp(`${column}\\s+([^,]+)`, 'i'))?.[1] || 'TEXT',
              }))

              // 테이블 정보 구성
              const tableInfo = {
                name: table,
                schema,
                values: data.values.map((row: Record<string, any>) => Object.values(row)),
              }

              exportData.tables.push(tableInfo)
              console.info(`Exported ${data.values.length} rows from ${table}`)
            }
          }
        } catch (error) {
          console.error(`Error exporting table ${table}:`, error)
        }
      }

      // JSON 데이터 암호화 (암호화되어 저장되므로 들여쓰기 없이 직렬화 — 크기 절감)
      const jsonString = JSON.stringify(exportData)

      // 안전장치: 직렬화 결과가 과대하면 백업 스킵 — 이후 암호화/base64/브리지 전달 과정에서
      // 메모리가 수 배로 증폭되어 OOM 크래시로 이어지므로, 크래시보다 백업 1회 스킵이 낫다
      const MAX_BACKUP_JSON_BYTES = 30 * 1024 * 1024
      if (jsonString.length > MAX_BACKUP_JSON_BYTES) {
        console.warn(
          `[Backup] 백업 데이터 과대(${(jsonString.length / 1024 / 1024).toFixed(1)}MB > 30MB) — 이번 백업 스킵`,
        )
        return
      }

      // 암호화·기록을 조각으로 나눠 사이사이 메인 스레드를 양보한다(편집 중 수 초 정지 방지).
      // 파일 내용은 예전(base64로 감싸 writeFile → 플러그인이 디코드)과 바이트 단위로 같다 — `iv:암호문` ASCII.
      const encryptedData = await encryptDataChunked(
        jsonString,
        import.meta.env.VITE_BACKUP_ENCRYPT_KEY,
      )
      const partPath = `${backupFullPath}${BACKUP_PART_SUFFIX}`

      try {
        for (let offset = 0; offset < encryptedData.length; offset += BACKUP_WRITE_CHUNK_CHARS) {
          const options = {
            path: partPath,
            data: encryptedData.slice(offset, offset + BACKUP_WRITE_CHUNK_CHARS),
            directory: Directory.Documents,
            encoding: Encoding.UTF8,
          }
          if (offset === 0) {
            await Filesystem.writeFile(options)
          } else {
            await Filesystem.appendFile(options)
          }
          await yieldToMain()
        }
        await Filesystem.rename({
          from: partPath,
          to: backupFullPath,
          directory: Directory.Documents,
        })
        console.info(
          `[Backup] 백업 완료: ${backupFullPath} (${(encryptedData.length / 1024 / 1024).toFixed(1)}MB)`,
        )
      } catch (writeError: unknown) {
        // 기록 도중 실패한 임시 파일은 바로 지운다 — 같은 원인(용량 부족 등)으로 계속 실패하면 정리 단계까지 못 가 쌓인다
        try {
          await Filesystem.deleteFile({ path: partPath, directory: Directory.Documents })
        } catch {
          // 아직 만들어지지 않았거나 이미 없음
        }
        const platform = Capacitor.getPlatform()
        let errorMessage: string

        if (writeError instanceof Error) {
          errorMessage = writeError.message
        } else {
          // Error 객체가 아닌 경우 문자열로 변환
          errorMessage = String(writeError)
        }

        // iOS 특정 에러 처리
        if (
          platform === 'ios' &&
          (errorMessage.includes('저장할 수 없습니다') ||
            errorMessage.includes('cannot save') ||
            errorMessage.includes('permission denied') ||
            errorMessage.includes('No such file or directory'))
        ) {
          console.error(`iOS 백업 파일 저장 에러: ${errorMessage}`)
          throw new Error(
            `iOS 백업 파일 저장 실패: 앱 권한 또는 경로를 확인해주세요. (${errorMessage})`,
          )
        }

        throw writeError
      }

      // 오래된 백업 파일 정리
      try {
        await cleanupOldBackups(taskName)
        console.info('Old backup cleanup completed')
      } catch (cleanupError) {
        // 정리 실패는 로그만 남기고 백업 자체는 성공으로 처리
        console.warn('Old backup cleanup failed:', cleanupError)
      }
    } catch (error) {
      console.error('Error creating backup:', error)
      throw error
    }
  } catch (error) {
    console.error('Backup failed:', error)
    throw error
  }
}

export const sendAllSqliteToServer = async (): Promise<void> => {
  try {
    const db = await SQL.getDatabase()
    // device_event_log 제외 — 백업과 동일하게, 로그가 수만 건 누적된 단말에서
    // 전량 직렬화가 브리지 OOM 크래시를 유발하므로 전송 대상에서 뺀다
    const tables = SQLite.getCurrentTables().filter(table => table !== 'device_event_log')

    // 디바이스 ID 가져오기
    const deviceId = await getDeviceOrClientId()

    // 현재 날짜/시간 포맷팅 (현지 시간대 반영)
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    const dateStr = `${year}${month}${day}_${hours}${minutes}`

    // 백업 데이터 구성
    const exportData: any = {
      database: 'all_data',
      version: 1,
      encrypted: true,
      mode: 'full',
      deviceId,
      timestamp: getUnixtime(),
      tables: [],
    }

    // 각 테이블의 데이터 수집
    for (const table of tables) {
      try {
        // 테이블 스키마 가져오기
        const schemaResult = await db.query(
          // eslint-disable-next-line quotes
          "SELECT sql FROM sqlite_master WHERE type='table' AND name=?",
          [table],
        )

        if (schemaResult.values && schemaResult.values.length > 0) {
          const createTableSQL = schemaResult.values[0].sql

          // 테이블 데이터 가져오기 (모든 데이터)
          const data = await db.query(`SELECT * FROM ${table}`)

          if (data.values && data.values.length > 0) {
            // 컬럼 정보 가져오기
            const columns = Object.keys(data.values[0])

            // 테이블 스키마 파싱
            const schema = columns.map(column => ({
              column,
              value: createTableSQL.match(new RegExp(`${column}\\s+([^,]+)`, 'i'))?.[1] || 'TEXT',
            }))

            // 테이블 정보 구성
            const tableInfo = {
              name: table,
              schema,
              values: data.values.map((row: Record<string, any>) => Object.values(row)),
              rowCount: data.values.length,
            }

            exportData.tables.push(tableInfo)
            console.info(`테이블 ${table}에서 ${data.values.length}개의 행을 내보냈습니다.`)
          } else {
            // 데이터가 없는 경우에도 스키마 정보는 포함
            exportData.tables.push({
              name: table,
              schema: [],
              values: [],
              rowCount: 0,
            })
            console.info(`테이블 ${table}에 데이터가 없습니다.`)
          }
        }
      } catch (error) {
        console.error(`테이블 ${table} 내보내기 중 오류:`, error)
      }
    }

    // JSON 데이터 암호화
    const jsonString = JSON.stringify(exportData)
    const encryptedData = encryptData(jsonString)

    // 네트워크 연결 확인
    const isNetworkConnected = (await Network.getStatus()).connected
    if (!isNetworkConnected) {
      App.toastInstance.add('error', '네트워크 연결이 없습니다. 백업 전송에 실패했습니다.')
      return
    }

    try {
      // 파일 이름 생성
      const fileName = `SQLiteBackup_${deviceId}_${dateStr}.json`

      // Blob 생성 후 File 객체로 변환
      const blob = new Blob([encryptedData], { type: 'application/json' })
      const file = new File([blob], fileName, { type: 'application/json' })

      // CloudFile을 사용하여 직접 업로드
      const resourceUrl = await CloudFile.uploadFile(
        'homecheck-service',
        `${GCS_ROOT_PATH}/sqlite-backup/${fileName}`,
        file,
      )

      if (resourceUrl) {
        App.toastInstance.add('success', '백업 데이터가 성공적으로 전송되었습니다.')
        console.info('백업 데이터 전송 성공:', resourceUrl)
      } else {
        throw new Error('업로드 URL을 받지 못했습니다.')
      }
    } catch (error) {
      console.error('백업 데이터 전송 중 오류:', error)
      App.toastInstance.add('error', `백업 전송 실패: ${(error as Error).message}`)
    }
  } catch (error) {
    console.error('SQLite 백업 생성 중 오류:', error)
    App.toastInstance.add('error', `백업 생성 실패: ${(error as Error).message}`)
  }
}

export const checkAppNetwork = async (
  toastInstance?: Toast,
  message?: string,
): Promise<boolean> => {
  const isNetworkConnected = (await Network.getStatus()).connected
  if (isApp() && !isNetworkConnected) {
    if (toastInstance) {
      const currentMessage = message || '네트워크 연결이 끊어졌습니다. 수정할 수 없습니다.'
      toastInstance.add('error', currentMessage)
    }
    return false
  }
  return true
}

export function getColorVariables(): {
  primary: Record<string, string>
  gray: Record<string, string>
  red: Record<string, string>
} {
  const pick = (name: string): Record<string, string> =>
    Object.fromEntries(
      Array.from({ length: 9 }, (_, i) => {
        const key = `${(i + 1) * 100}`
        const val = getComputedStyle(document.documentElement)
          .getPropertyValue(`--${name}-${key}`)
          .trim()
        return [key, val]
      }),
    )

  return {
    primary: pick('Primary'),
    gray: pick('Gray'),
    red: pick('Red'),
  }
}

export const onClickHelp = (): void => {
  window.open('https://forms.gle/2qQdJzRKtkvefmBt5', '_blank')
}

export const getArrFirstItem = (arr: string[]): number => {
  if (!arr || arr.length == 0) return getUnixtime()
  return Number(arr[0])
}

export const isPreviousPhoto = (photo: any): boolean => {
  if (!photo || !photo.url) return false
  const targetList = ['/defect/', '/prev/', '_prev']
  return (
    targetList.some(target => photo.url.includes(target)) ||
    photo.is_previous != 0 ||
    photo.is_previous
  )
}

export const isOutSidePhoto = (photo: DefectPhoto): boolean => {
  return !photo.inspection_type || photo.inspection_type == 'STATUS_OUTSIDE'
}

export const getDeviceOrClientId = async (): Promise<string> => {
  if (isApp()) {
    const deviceInfo = await Device.getId()
    const deviceId = deviceInfo.identifier ? `app_${deviceInfo.identifier}` : 'app_unknown'
    return deviceId
  }
  let clientId = localStorage.getItem('CLIENT_ID')
  if (!clientId) {
    clientId = `web_${getRandomString(10, false)}`
    localStorage.setItem('CLIENT_ID', clientId)
  }
  return clientId
}

/**
 * 온보딩 완료 여부 확인 (주어진 stepKeys가 모두 저장되어 있으면 true)
 * @param groupKey 온보딩 그룹 키
 * @param stepKeys 확인할 스텝 키 배열
 */
export const isEndedOnBoarding = async (groupKey: string, stepKeys: string[]): Promise<boolean> => {
  try {
    const clientId = await getDeviceOrClientId()
    const stored = localStorage.getItem(`${groupKey}_${clientId}`)
    if (!stored) return false
    const parsed: unknown = JSON.parse(stored)
    const endedKeys: string[] = Array.isArray(parsed) ? parsed : []
    return stepKeys.every(key => endedKeys.includes(key))
  } catch (error) {
    console.error('Failed to check onboarding status:', error)
    return false
  }
}

/**
 * 온보딩 완료 상태 저장 (주어진 stepKeys를 기존 값과 merge)
 * @param groupKey 온보딩 그룹 키
 * @param stepKeys 저장할 스텝 키 배열
 */
export const setEndedOnBoarding = async (groupKey: string, stepKeys: string[]): Promise<void> => {
  try {
    const clientId = await getDeviceOrClientId()
    const stored = localStorage.getItem(`${groupKey}_${clientId}`)
    const parsed: unknown = stored ? JSON.parse(stored) : []
    const existingKeys: string[] = Array.isArray(parsed) ? parsed : []
    const merged = [...new Set([...existingKeys, ...stepKeys])]
    localStorage.setItem(`${groupKey}_${clientId}`, JSON.stringify(merged))
  } catch (error) {
    console.error('Failed to set onboarding status:', error)
  }
}

/**
 * 미완료 step의 시작 인덱스 반환 (모두 완료면 -1)
 * @param groupKey 온보딩 그룹 키
 * @param stepKeys 확인할 스텝 키 배열
 */
export const getFirstUnseenStepIndex = async (
  groupKey: string,
  stepKeys: string[],
): Promise<number> => {
  try {
    const clientId = await getDeviceOrClientId()
    const stored = localStorage.getItem(`${groupKey}_${clientId}`)
    if (!stored) return 0
    const parsed: unknown = JSON.parse(stored)
    const endedKeys: string[] = Array.isArray(parsed) ? parsed : []
    return stepKeys.findIndex(key => !endedKeys.includes(key))
  } catch (error) {
    console.error('Failed to get first unseen step:', error)
    return 0
  }
}

//타임스탬프를 받아 연도만 출력
export const getYearFromTimestamp = (timestamp: number): number => {
  const date = new Date(timestamp * 1000)
  return date.getFullYear()
}

export const goToMain = async (): Promise<void> => {
  await App.Router.push('/')
}

export const isPreviousShape = (previous_id: number | string | null): boolean => {
  // undefined, null, 빈 문자열 처리
  if (previous_id === undefined || previous_id === null || previous_id === '') {
    return false
  }
  const numValue = Number(previous_id)
  // NaN 체크 및 0 이상인지 확인
  return !isNaN(numValue) && numValue >= 0
}

// 전차이면서 현재차 사진도 없고 is_current도 아닌 순수 전차 여부
// inspection_type이 일치하는 사진만 현재차 사진으로 간주
export const isPrevDefect = (shape: Defect, photos: DefectPhoto[]): boolean => {
  const shapeType = shape.inspection_type ?? 'STATUS_OUTSIDE'
  return (
    isPreviousShape(shape.previous_defect_id) &&
    !photos.some(
      photo => !isPreviousPhoto(photo) && (photo.inspection_type ?? 'STATUS_OUTSIDE') === shapeType,
    ) &&
    !shape.is_current
  )
}

export const cutStringLength = (text: string, length: number): string => {
  if (!text) return ''
  if (text.length <= length) return text
  return text.substring(0, length)
}

export const removeExtension = (fileName: string): string => {
  // 빈 문자열이나 점만 있는 경우 처리
  if (!fileName || fileName === '.' || fileName === '..') {
    return fileName
  }

  // 숨겨진 파일(.으로 시작) 처리
  if (fileName.startsWith('.') && !fileName.includes('.', 1)) {
    return fileName // 확장자가 없는 숨겨진 파일
  }

  const lastDotIndex = fileName.lastIndexOf('.')

  // 점이 없거나 맨 앞에 있는 경우
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return fileName
  }

  return fileName.substring(0, lastDotIndex)
}

export const textLengthCheck = (
  text: string,
  type?: 'task' | 'facility' | 'input_item' | 'shape' | 'memo' | null,
  maxlength?: number,
): boolean => {
  const toast = App.toastInstance
  if (!text) {
    return true
  }
  if (!type) {
    if (maxlength && text.length > maxlength) {
      toast.add('error', `${maxlength}자 이내로 입력해주세요.`)
      return false
    }
    return true
  }
  switch (type) {
    case 'task':
      if (text.length > 20) {
        toast.add('error', '20자 이내로 입력해주세요.')
        return false
      }
      return true
    case 'facility':
      if (text.length > 50) {
        toast.add('error', '50자 이내로 입력해주세요.')
        return false
      }
      return true
    case 'input_item':
    case 'shape':
      if (text.length > 30) {
        toast.add('error', '30자 이내로 입력해주세요.')
        return false
      }
      return true
    default:
      return true
  }
}

export function getLastInSync(taskId: string): number {
  const currentSyncs = localStorage.getItem('CURRENT_SYNC_TASK_IDS')
  if (!currentSyncs) return 0
  const currentSyncsArray = JSON.parse(currentSyncs)
  const item = currentSyncsArray.find((item: any) => item.taskId === taskId)
  if (!item) return 0
  return item.lastInSync
}

export async function setLastInSync(taskId: string): Promise<void> {
  if (!(await Network.getStatus()).connected) {
    return
  }
  // 최근 동기화 task 보존 상한 (eviction 보호 대상 MAX_RECENT_TASKS=4보다 넉넉)
  const MAX_SYNC_TASK_IDS = 30
  const currentSyncs = localStorage.getItem('CURRENT_SYNC_TASK_IDS')
  let currentSyncsArray: { taskId: string; lastInSync: number }[] = []
  if (currentSyncs) {
    try {
      currentSyncsArray = JSON.parse(currentSyncs)
    } catch {
      currentSyncsArray = []
    }
  }
  // 손상되어 배열이 아닌 값이 들어있어도 이후 배열 메서드에서 throw하지 않도록 방어
  if (!Array.isArray(currentSyncsArray)) currentSyncsArray = []
  const existing = currentSyncsArray.find(item => item.taskId === taskId)
  if (existing) {
    existing.lastInSync = getUnixtime()
  } else {
    currentSyncsArray.push({ taskId, lastInSync: getUnixtime() })
  }
  // 최신순 정렬 후 상한 적용
  currentSyncsArray.sort((a, b) => b.lastInSync - a.lastInSync)
  if (currentSyncsArray.length > MAX_SYNC_TASK_IDS) {
    currentSyncsArray = currentSyncsArray.slice(0, MAX_SYNC_TASK_IDS)
  }
  localStorage.setItem('CURRENT_SYNC_TASK_IDS', JSON.stringify(currentSyncsArray))
}

export function parseUnixTimeToDate(unixTime: number): string {
  const date = new Date(unixTime * 1000)
  // YY.MM.DD HH:MM 형식으로 표시 (24시간)
  return date
    .toLocaleString('ko-KR', {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replaceAll('. ', '.') // "24.07.24. 15:20" → "24.07.24.15:20"
    .replace(/(\d{2}\.\d{2}\.\d{2})\./, '$1 ') // 마지막 점을 공백으로
}

export function getLastConnectTime(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp

  // 1분 미만
  if (diffMs < 60 * 1000) {
    return '방금 전'
  }
  // 1시간 미만
  else if (diffMs < 60 * 60 * 1000) {
    const minutes = Math.floor(diffMs / (60 * 1000))
    return `${minutes}분 전`
  }
  // 24시간 미만
  else if (diffMs < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diffMs / (60 * 60 * 1000))
    return `${hours}시간 전`
  }
  // 그 이상
  else {
    const date = new Date(timestamp * 1000)
    return `${date.getMonth() + 1}월 ${date.getDate()}일 ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
  }
}

/**
 * timestamp 문자열을 상대적인 시간으로 변환하는 함수
 * @param {number} timestamp - timestamp
 * @returns {string} - 상대적인 시간 표현
 */
export const formatRelativeTimeStamp = (timestamp: string): string => {
  const now = new Date()

  const createdAt = new Date(timestamp)

  const diffInMs = now.getTime() - createdAt.getTime()

  const minutes = Math.floor(diffInMs / (1000 * 60))
  const hours = Math.floor(diffInMs / (1000 * 60 * 60))
  const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  const months = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 30))

  if (minutes < 1) {
    return '방금 전'
  } else if (minutes < 60) {
    return `${minutes}분 전`
  } else if (hours < 24) {
    return `${hours}시간 전`
  } else if (days < 30) {
    return `${days}일 전`
  } else {
    return `${months}개월 전`
  }
}

export const getItemToProcess = async (
  datas: any[],
  storeKey: string,
): Promise<{ itemsToProcess: { item: any; itemId: string }[]; columns: string[] }> => {
  try {
    const db = await SQL.getDatabase()

    // 1. 테이블 컬럼 정보 (캐시 우선)
    let columns: string[] | undefined = columnCache.get(storeKey)
    if (!columns) {
      let columnsResult = await db.query(`PRAGMA table_info(${storeKey})`)
      const tableExists = columnsResult.values?.length > 0

      // 테이블이 없거나 createdTables에 없는 경우 생성
      if (!tableExists || !SQL.createdTables.has(storeKey)) {
        const createTableSQL = await getDynamicTableSql(storeKey, datas)
        if (createTableSQL && createTableSQL.trim()) {
          await db.execute(createTableSQL)
          SQL.createdTables.add(storeKey)
        }

        // 테이블을 새로 생성했으므로 컬럼 정보 다시 가져오기
        if (!tableExists) {
          const newColumnsResult = await db.query(`PRAGMA table_info(${storeKey})`)
          if (!newColumnsResult.values?.length) {
            return {
              itemsToProcess: [],
              columns: [],
            }
          }
          columnsResult = newColumnsResult
        }
      }

      columns = (columnsResult.values ?? []).map((col: any) => col.name) as string[]
      columnCache.set(storeKey, columns)
      columnAffinityCache.set(
        storeKey,
        new Map(
          (columnsResult.values ?? []).map((col: any) => [col.name, getColumnAffinity(col.type)]),
        ),
      )
    }

    // columns는 캐시 히트 또는 위 블록에서 반드시 할당됨
    const resolvedColumns = columns!

    // 2. 중복 ID 제거 및 처리할 데이터 준비
    const processedIds = new Set<string>()
    const itemsToProcess = []

    for (const item of datas) {
      const rawId = item.id !== undefined ? item.id : getRandomString(16, false)
      // 타입 시그니처 (itemId: string) 와 일치시키기 위해 항상 string 으로 정규화.
      // iOS Capacitor SQLite bridge 가 number 를 "557.0" 형태로 stringify 하는 문제 차단.
      const itemId = typeof rawId === 'number' ? String(rawId) : rawId
      if (processedIds.has(itemId.toString())) continue
      processedIds.add(itemId.toString())
      itemsToProcess.push({
        item,
        itemId,
      })
    }
    if (itemsToProcess.length === 0) {
      return {
        itemsToProcess: [],
        columns: [],
      }
    }

    return {
      itemsToProcess,
      columns: resolvedColumns,
    }
  } catch (e) {
    console.error('Error in getItemToProcess:', e)
    return {
      itemsToProcess: [],
      columns: [],
    }
  }
}
//getUnixtime의 값에 -1을 곱하는 함수
export function getMinusOneUnixtime(): number {
  const unixTime = getUnixtime()
  return unixTime * -1
}

export function isUndoItem(deletedTime: string | null): boolean {
  if (!deletedTime) return false
  return deletedTime !== null && deletedTime !== undefined
}

export function setQuickInput(item: QuickInputItem): void {
  // QUICK_INPUT은 사용자가 직접 추가/삭제하는 프리셋 목록 (자동 누적 없음, 상한 미적용)
  const quickInput = localStorage.getItem('QUICK_INPUT')
  let quickInputArray: QuickInputItem[] = []
  if (quickInput) {
    try {
      quickInputArray = JSON.parse(quickInput)
    } catch {
      // 파싱 실패 시 기존 데이터를 덮어쓰지 않고 추가만 건너뛴다(프리셋 보존 우선)
      console.warn('[QuickInput] QUICK_INPUT 파싱 실패 — 추가 건너뜀')
      return
    }
  }
  // 배열이 아니면(손상) 기존 값을 덮어쓰지 않고 건너뜀
  if (!Array.isArray(quickInputArray)) {
    console.warn('[QuickInput] QUICK_INPUT 형식 오류 — 추가 건너뜀')
    return
  }
  quickInputArray.push(item)
  localStorage.setItem('QUICK_INPUT', JSON.stringify(quickInputArray))
}

export function getQuickInputs(): QuickInputItem[] {
  const quickInput = localStorage.getItem('QUICK_INPUT')
  if (!quickInput) return []
  return JSON.parse(quickInput)
}

export function removeQuickInput(item: QuickInputItem): void {
  const quickInput = localStorage.getItem('QUICK_INPUT')
  if (!quickInput) return
  const quickInputArray = JSON.parse(quickInput)
  const index = quickInputArray.findIndex(
    (quickInputItem: QuickInputItem) => quickInputItem.id === item.id,
  )
  if (index !== -1) {
    quickInputArray.splice(index, 1)
    localStorage.setItem('QUICK_INPUT', JSON.stringify(quickInputArray))
  }
}

export function getConfigValue(
  config: UserSettingConfigSettings[],
  key: string,
): number | string | boolean | (number | string | boolean)[] | null {
  const returnValue = get(find(config, { key }), 'value')
  if (returnValue === undefined || returnValue === null) return null
  return returnValue
}

export function convertUserSettingToCanvasStore(userSetting: UserSettingConfig[]): any {
  const styleConfig = userSetting.find(item => item.key === 'style')?.settings
  if (!styleConfig) return

  let userSetBalloonScale: number = 1.0
  let userSetConnectedLineThicknessScale: number = 1.0
  let userSetBalloonFontScale: number = 1.0
  let userSetPointShapeScale: number = 1.0
  let userSetAreaBorderThicknessScale: number = 1.0
  let userSetBalloonStyle: string[] = ['red', 'none', 'red']
  let userSetBalloonFillOpacity: number = 1

  styleConfig.forEach((item: any) => {
    switch (item.key) {
      case 'balloonScale':
        userSetBalloonScale = item.value
        break
      case 'connectedLineThicknessScale':
        userSetConnectedLineThicknessScale = item.value
        break
      case 'balloonFontScale':
        userSetBalloonFontScale = item.value
        break
      case 'pointShapeScale':
        userSetPointShapeScale = item.value
        break
      case 'areaBorderThicknessScale':
        userSetAreaBorderThicknessScale = item.value
        break
      case 'balloonStyle':
        userSetBalloonStyle = item.value
        break
      case 'balloonFillOpacity':
        userSetBalloonFillOpacity = item.value
        break
    }
  })
  return {
    userSetBalloonScale,
    userSetConnectedLineThicknessScale,
    userSetBalloonFontScale,
    userSetPointShapeScale,
    userSetAreaBorderThicknessScale,
    userSetBalloonStyleBordeColor: userSetBalloonStyle[0] as string,
    userSetBalloonStyleFillColor: userSetBalloonStyle[1] as string,
    userSetBalloonStyleFontColor: userSetBalloonStyle[2] as string,
    userSetBalloonFillOpacity,
  }
}

// 현재 백버튼 리스너와 활성화된 콜백 함수 저장
let backButtonListener: any = null
let currentBackButtonCallback: (() => void) | null = null

// 백버튼 이벤트 핸들러 (한 번만 등록)
const handleBackButton = (_data: any): void => {
  if (currentBackButtonCallback) {
    currentBackButtonCallback()
  } else {
    // 커스텀 콜백 미등록 시 기본 동작
    window.history.back()
  }
}

/** 전역 백버튼 리스너 초기화 (앱 시작시 한 번만 실행) */
export const initBackButtonListener = (): void => {
  if (!backButtonListener && isApp()) {
    backButtonListener = CapacitorApp.addListener('backButton', handleBackButton)
  }
}

export const pressDeviceBackButton = (func: () => void): string => {
  const callbackId = Math.random().toString(36).slice(2, 11)

  if (isApp()) {
    // 전역 리스너가 없다면 초기화
    initBackButtonListener()

    // 현재 활성화될 콜백 함수와 ID 설정
    currentBackButtonCallback = func
  }

  return callbackId
}

export const removeBackButtonCallback = (): void => {
  if (!isApp()) return

  currentBackButtonCallback = null
  // backButtonListener는 유지 (리스너 중복 등록 방지)
}

/**
 * 타임스탬프를 Unix timestamp 숫자로 정규화 (초 단위)
 * @param {number | string | Date | undefined | null} timestamp - 정규화할 타임스탬프
 * @returns {number} - Unix timestamp (초 단위)
 */
function normalizeTimestamp(timestamp: number | string | Date | undefined | null): number {
  if (!timestamp) return 0
  // 숫자인 경우: 자릿수로 밀리초/초 판단
  if (typeof timestamp === 'number') {
    // 13자리 이상이면 밀리초로 간주
    return timestamp > 9999999999 ? Math.floor(timestamp / 1000) : timestamp
  }
  // Date 객체인 경우: 밀리초를 초로 변환
  if (timestamp instanceof Date) return Math.floor(timestamp.getTime() / 1000)
  // 문자열인 경우: getUnixtimeFromString 사용 (이미 초 단위 반환)
  if (typeof timestamp === 'string') return getUnixtimeFromString(timestamp)
  return 0
}

export const getProgressingText = (
  progressing: number,
): { text: string; type: 'pending' | 'progressing' | 'completed' | 'failed' | 'canceled' } => {
  if (progressing === undefined || progressing === null) return { text: '-', type: 'pending' }
  switch (progressing) {
    case 0:
      return { text: '대기', type: 'pending' }
    case 1:
      return { text: '진행중', type: 'progressing' }
    case 2:
      return { text: '완료', type: 'completed' }
    case 3:
      return { text: '실패', type: 'failed' }
    case 4:
      return { text: '취소', type: 'canceled' }
    default:
      return { text: '-', type: 'pending' }
  }
}

export const uploadPathGenerator = (
  taskId: string,
  facilityId?: string,
  floorplanId?: string,
): string => {
  if (!taskId) {
    throw new Error('taskId is required')
  }

  if (facilityId) {
    return `task/${taskId}/facility_branch/${facilityId}`
  }

  if (facilityId && floorplanId) {
    return `task/${taskId}/facility_branch/${facilityId}/floor_plan/${floorplanId}`
  }

  return `task/${taskId}`
}

export const isChangedMetaDataValue = (
  modifiedTag: string | null,
  settingVal: number | string | null,
  metaDataVal: number | string | null,
  forceUpdate: () => void,
): void => {
  if (!modifiedTag && settingVal && settingVal != metaDataVal) {
    forceUpdate()
    return
  }
  return
}

/**
 * BASE62 문자 순서를 기반으로 두 문자열을 비교하는 함수
 * fractional-indexing 라이브러리의 정렬 방식과 동일
 *
 * BASE62 순서: 0-9 < A-Z < a-z
 *
 * @param a - 첫 번째 문자열
 * @param b - 두 번째 문자열
 * @returns 비교 결과 (-1, 0, 1)
 */
const compareBase62 = (a: string, b: string): number => {
  const BASE62_DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

  const maxLen = Math.max(a.length, b.length)

  for (let i = 0; i < maxLen; i++) {
    const aChar = a[i] || ''
    const bChar = b[i] || ''

    // 한쪽이 끝난 경우 (짧은 문자열이 앞)
    if (!aChar) return -1
    if (!bChar) return 1

    const aIndex = BASE62_DIGITS.indexOf(aChar)
    const bIndex = BASE62_DIGITS.indexOf(bChar)

    if (aIndex !== bIndex) {
      return aIndex - bIndex
    }
  }

  return 0
}

/**
 * Fractional-indexing의 sort_key와 created_at을 기준으로 배열을 정렬하는 함수
 *
 * 정렬 우선순위:
 * 1. sort_key (BASE62 정렬 - fractional-indexing 방식)
 * 2. sort_key가 같거나 없으면 created_at (날짜/숫자 비교)
 * 3. 둘 다 없으면 원래 순서 유지
 *
 * @param data - 정렬할 데이터 배열
 * @returns 정렬된 데이터 배열
 */
export const sortByKeys = <
  T extends { sort_key?: string | null; created_at?: string | number | Date | null },
>(
  data: T[],
): T[] => {
  return data.sort((a, b) => {
    const aSortKey = a.sort_key || ''
    const bSortKey = b.sort_key || ''

    // 1. sort_key로 우선 정렬 (BASE62 정렬 방식)
    if (aSortKey && bSortKey) {
      const sortKeyComparison = compareBase62(aSortKey, bSortKey)
      if (sortKeyComparison !== 0) {
        return sortKeyComparison
      }
    }

    // 2. sort_key가 같거나 둘 중 하나라도 없으면 created_at으로 정렬
    const aCreatedAt = normalizeTimestamp(a.created_at)
    const bCreatedAt = normalizeTimestamp(b.created_at)

    if (aCreatedAt !== bCreatedAt) {
      return aCreatedAt - bCreatedAt
    }

    // 3. 둘 다 같으면 원래 순서 유지
    return 0
  })
}
export const getExtension = (file: File): string => {
  const split = file.name.split('.')
  return split[split.length - 1]
}
/**
 * 배열의 마지막 항목보다 뒤에 올 sort_key를 생성하는 함수
 *
 * @param data - 정렬할 데이터 배열
 * @returns 마지막 항목 뒤의 sort_key
 */
export const getLastSortKey = <
  T extends { sort_key?: string | null; created_at?: string | number | Date | null },
>(
  data: T[],
): string => {
  if (!data || data.length === 0) return generateKeyBetween(null, null)
  const sortedData = sortByKeys(data)
  const lastItem = sortedData[sortedData.length - 1]
  if (!lastItem) return generateKeyBetween(null, null)

  try {
    return generateKeyBetween(lastItem.sort_key, null)
  } catch (error) {
    console.warn(
      `[getLastSortKey] Invalid sort_key detected: ${lastItem.sort_key}. Generating new key.`,
      error,
    )
    return generateKeyBetween(null, null)
  }
}
/**
 * 배열의 첫 번째 항목보다 앞에 올 sort_key를 생성하는 함수
 *
 * @param data - 정렬할 데이터 배열
 * @returns 첫 번째 항목 앞의 sort_key
 */
export const getFirstSortKey = <
  T extends { sort_key?: string | null; created_at?: string | number | Date | null },
>(
  data: T[],
): string => {
  if (!data || data.length === 0) return generateKeyBetween(null, null)
  const sortedData = sortByKeys(data)
  const firstItem = sortedData[0]
  if (!firstItem) return generateKeyBetween(null, null)

  try {
    return generateKeyBetween(null, firstItem.sort_key)
  } catch (error) {
    console.warn(
      `[getFirstSortKey] Invalid sort_key detected: ${firstItem.sort_key}. Generating new key.`,
      error,
    )
    return generateKeyBetween(null, null)
  }
}
function toKST(utcString: string): string {
  const date = new Date(utcString) // UTC 기준으로 파싱됨
  // 한국시간(UTC+9)으로 변환한 ISO 문자열 생성
  const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000)
  const iso = kst.toISOString() // 항상 Z로 끝나는 형식

  // Z를 +09:00으로 교체
  return iso.replace('Z', '+09:00')
}

/**
 *
 * @param time - UTC 시간
 * @returns 한국 시간 MM.DD HH:MM 형식의 문자열
 */
export const getCurrentTimeForVoiceFile = (time: string): string => {
  // 한국 시간으로 변호나
  const kstTime = toKST(time)
  const date = new Date(kstTime)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${month}.${day} ${hours}:${minutes}`
}

export const getConvertedTaskName = (item: Task): string => {
  if (!item) return ''
  if (item.year) {
    return `${item?.year ?? ''} ${item?.period ?? ''} ${item?.name ?? ''} ${item?.type ?? ''}`
  }
  return `${item?.period ?? ''} ${item?.name ?? ''} ${item?.type ?? ''}`
}

/**
 * 배열의 모든 항목에 대해 순차적인 sort_key를 생성하여 할당하는 함수
 *
 * @param data - sort_key를 생성할 데이터 배열
 * @param lastSortKey - 시작점이 될 sort_key (이 키 다음부터 생성)
 * @returns sort_key가 새로 할당된 데이터 배열
 */
export const generateSortKeyArr = <T extends { sort_key?: string | null }>(
  data: T[],
  lastSortKey?: string | null,
): T[] => {
  // 빈 배열 처리
  if (!data || data.length === 0) return []

  const newSortKeys = data.reduce<string[]>((keys: string[], _: T, index: number): string[] => {
    // 첫 번째 항목은 lastSortKey 다음부터, 이후는 이전 키 다음부터
    const prevKey = index === 0 ? (lastSortKey ?? null) : keys[index - 1]

    try {
      keys.push(generateKeyBetween(prevKey, null))
    } catch (error) {
      console.warn(
        `[generateSortKeyArr] Invalid prevKey detected at index ${index}: ${prevKey}. Generating new key.`,
        error,
      )
      // 에러 발생 시 이전 키를 무시하고 새로운 키 생성
      keys.push(generateKeyBetween(null, null))
    }

    return keys
  }, [])

  const newData = data.map((item: T, index: number): T => {
    return {
      ...item,
      sort_key: newSortKeys[index] ?? generateKeyBetween(null, null),
    }
  })

  return newData
}

/**
 * Task 객체에서 반기정보 반환
 *
 * @param task - Task 객체
 * @returns 현재 반기와 이전 반기 문자열 객체
 */
export function getHalfYearFromTask(task: Task): { current: string; previous: string } {
  // Task에서 년도와 반기 추출하는 헬퍼 함수
  const extractYearAndHalf = (t: Task): { year: number; isFirstHalf: boolean } => {
    let year: number = t.year || new Date().getFullYear()
    let isFirstHalf: boolean

    if (t.period === '상반기') isFirstHalf = true
    else if (t.period === '하반기') isFirstHalf = false
    else {
      const createdAt = new Date(t.created_at)
      const month = createdAt.getMonth() + 1
      isFirstHalf = month <= 6
      if (!t.year) year = createdAt.getFullYear()
    }

    return { year, isFirstHalf }
  }

  // 현재 정보 추출
  const { year: currentYear, isFirstHalf } = extractYearAndHalf(task)
  const current: string = `${currentYear}년 ${isFirstHalf ? '상반기' : '하반기'}`

  // 이전 정보 추출
  let previous: string
  const previousTask = task.previous_task_id
    ? useAllTaskStore().getTaskList.find(t => t.id === task.previous_task_id)
    : null

  if (previousTask) {
    // 이전 task가 있으면 해당 task의 정보로 생성
    const { year: prevYear, isFirstHalf: prevIsFirstHalf } = extractYearAndHalf(previousTask)
    previous = `${prevYear}년 ${prevIsFirstHalf ? '상반기' : '하반기'}`
  } else {
    // 이전 task를 찾지 못하면 현재 정보 기반으로 직전 계산
    previous = isFirstHalf ? `${currentYear - 1}년 하반기` : `${currentYear}년 상반기`
  }

  return { current, previous }
}

export async function openBrowser(url: string): Promise<void> {
  if (isApp()) {
    await Browser.open({ url })
  } else {
    window.open(url, '_blank')
  }
}

/**
 * 촬영한 사진을 갤러리에서 앱 저장소로 복사
 * @param data 사진 데이터 (resourcePath 속성 포함)
 * @param updateCallback SQLite 업데이트 콜백 함수
 */
export async function handleCapturedPhotoClone<
  T extends { resourcePath?: string; localStringId: string },
>(data: T): Promise<void> {
  const { Capacitor } = await import('@capacitor/core')
  const { saveCapturedPhoto } = await import('@/app/native')

  if (!Capacitor.isNativePlatform() || !data.resourcePath?.startsWith('local-file://')) {
    return
  }

  const cleanPath = data.resourcePath.replace('local-file://', '')
  // Android: DCIM/cache 경로, iOS: 임시 디렉토리(/tmp) 경로 확인
  if (
    !cleanPath.includes('/DCIM/') &&
    !cleanPath.includes('/tmp/') &&
    !cleanPath.includes('/cache/') &&
    !cleanPath.includes(`/${PHOTO_DIR}/`)
  ) {
    return
  }

  try {
    const fileName = data.resourcePath.split('/').pop()
    if (!fileName) {
      return
    }

    await saveCapturedPhoto({
      fileName,
      oldPath: data.resourcePath,
      dir: PHOTO_DIR,
    })
  } catch (error) {
    console.error('saveCapturedPhoto 실패:', error)
    // 업로드는 성공했으므로 에러를 throw하지 않음
  }
}

/**
 * 서버 업로드 후 캐시에 서버 파일명으로 복사
 * @param localPath local-file:// 경로
 * @param serverUrl storage:// URL
 */
export async function syncCacheWithServerUrl(localPath: string, serverUrl: string): Promise<void> {
  if (!serverUrl.startsWith('storage://')) return

  const { Capacitor } = await import('@capacitor/core')
  if (!Capacitor.isNativePlatform()) return

  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem')

    // storage:// URL에서 파일명 추출
    const serverFileName = serverUrl.split('/').pop()
    if (!serverFileName) {
      return
    }

    // 같은 파일이 이미 있으면 스킵
    try {
      await Filesystem.stat({
        path: `${PHOTO_DIR}/${serverFileName}`,
        directory: Directory.Cache,
      })
      // 파일이 이미 존재하면 복사하지 않음
      return
    } catch {
      // 파일이 없으면 계속 진행
    }

    // localPath에서 파일명 추출
    const localFileName = localPath.replace('local-file://', '').split('/').pop()
    if (!localFileName) {
      return
    }

    // 파일 읽기
    const fileContent = await Filesystem.readFile({
      path: `${PHOTO_DIR}/${localFileName}`,
      directory: Directory.Cache,
    })

    // 서버 파일명으로 저장
    await Filesystem.writeFile({
      path: `${PHOTO_DIR}/${serverFileName}`,
      data: fileContent.data,
      directory: Directory.Cache,
    })
  } catch (error) {
    console.error('캐시 동기화 실패:', error)
    // 실패해도 에러를 throw하지 않음 (서버 업로드는 성공했으므로)
  }
}

/**
 * 사용자가 의도적으로 취소한 에러인지 확인
 * @param error 에러 객체
 * @returns 취소 에러인 경우 true
 */
export function isUserCancellation(error: any): boolean {
  const message = error?.message || String(error)
  return (
    message.includes('취소') ||
    message.toLowerCase().includes('cancel') ||
    message.toLowerCase().includes('cancelled')
  )
}

/**
 * 리소스 경로를 local-file:// 형식으로 정규화
 * @param resourcePath 변환할 리소스 경로
 * @returns local-file:// 프리픽스로 정규화된 경로
 */
export function normalizeResourcePath(resourcePath: string): string {
  if (resourcePath.startsWith('local-file://')) {
    return resourcePath
  }
  return resourcePath
    .replace('android-local-file://', 'local-file://')
    .replace('android-file://', 'local-file://')
    .replace('file://', 'local-file://')
}

// 중복 실행 방지 플래그
let isCheckingNativeUpdate = false

// 필수 업데이트 확인 (업데이트가 필요할시 스토어 모달)
// 에러 = 스킵 (상위로 스로 전파하지 않음)
export async function checkRequiredNativeUpdate(): Promise<void> {
  let shouldOpenModal = false
  try {
    // 이미 실행 중이면 스킵
    if (isCheckingNativeUpdate) throw new Error('이미 실행 중')

    // 앱이고 네트워크가 있을때만 처리
    if (!isApp()) throw new Error('앱 환경이 아님')

    const networkStatus = await Network.getStatus()
    if (!networkStatus.connected) throw new Error('네트워크 연결 안됨')

    isCheckingNativeUpdate = true

    // Lib에서 버전정보 가져오기
    const { webVersion, appVersion, platform } = Lib

    // 플랫폼 확인
    if (platform !== 'ios' && platform !== 'android') {
      throw new Error(`지원하지 않는 플랫폼: ${platform}`)
    }

    // 버전 정보 확인
    if (!webVersion || !appVersion) throw new Error('버전 정보가 없음')

    // 쿼리파람
    const buildType: 'dev' | 'production' = IS_PRODUCTION ? 'production' : 'dev'
    const params = new URLSearchParams({
      build_type: buildType,
      version: webVersion,
      order: 'created_at:desc',
      limit: '1',
    })

    // api로 요청 (회색지대 네트워크에서 무한 대기하지 않도록 타임아웃 적용)
    const signal =
      typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(10_000) : undefined
    const response = await fetch(`${API_URL}public/app-versions?${params.toString()}`, { signal })
    if (!response.ok) throw new Error(`API 요청 실패: ${response.status}`)

    const result = await response.json()
    if (result.statusCode !== 200 || !result.data || result.data.length === 0) {
      throw new Error('서버에서 버전 정보를 찾을 수 없음')
    }

    // 가장 최근 행
    const latestRecord = result.data[0]

    // 플랫폼별 필요 버전 확인
    const requiredVersion =
      platform === 'ios' ? latestRecord.ios_version : latestRecord.android_version

    // 앱 버전이 없으면 (네이티브 버전관리 전 버전행들은 null) 종료
    if (!requiredVersion) throw new Error('버전 정보 없음')

    // 버전 비교
    const needsUpdate = appVersion !== requiredVersion
    if (needsUpdate) {
      // 모든 포커스 제거 (모달 뒤에서 인풋 포커스 방지)
      const activeElement = document.activeElement as HTMLElement
      if (activeElement && activeElement.blur) {
        activeElement.blur()
      }
      document.querySelectorAll('input, textarea').forEach(el => {
        ;(el as HTMLElement).blur()
      })

      // 이미 업데이트 모달이 열려있으면 재호출 스킵
      const confirmState = App.confirmInstance.get().value
      if (confirmState.isShow && confirmState.isRequiredUpdateModal) {
        shouldOpenModal = true
        return
      }

      await App.confirmInstance.show({
        message: `더 안정적인 서비스 이용을 위해\n최신 버전(${requiredVersion})으로 업데이트가 필요합니다.`,
        highlightText: '필수 업데이트 안내',
        cancelText: '',
        confirmText: '업데이트',
        isRequiredUpdateModal: true,
        onConfirm: async () => {
          if (platform === 'android') {
            window.location.href = 'market://details?id=kr.homecheck.zentrix'
          } else if (platform === 'ios') {
            // iOS: App Store URL
            await openBrowser('https://apps.apple.com/app/id6751304559')
          }
        },
      })

      shouldOpenModal = true
    }
  } catch (error) {
    console.error('[checkRequiredNativeUpdate] 에러:', error)
  } finally {
    isCheckingNativeUpdate = false
    // 모달을 열지 않은 경우에만 닫기
    if (!shouldOpenModal) {
      const confirmState = App.confirmInstance.get().value
      if (confirmState.isShow && confirmState.isRequiredUpdateModal) {
        confirmState.isShow = false
      }
    }
  }
}

// 네트워크 연결 확인
export const checkNetworkConnection = async (): Promise<boolean> => {
  try {
    const status = await Network.getStatus()
    return status.connected
  } catch {
    return navigator.onLine
  }
}
